import { Box } from '../primitives/Box.js';
import { HStack, VStack } from '../primitives/Stack.js';
import { Text } from '../primitives/Text.js';
import { cx } from '../primitives/cx.js';

export type HeatCell = {
  /** 0–1. Zero with `promised` false is a rest day, not a failure. */
  ratio: number;
  /** Whether anything was asked of this day at all. */
  promised: boolean;
  future: boolean;
  isToday: boolean;
  /** Spoken on hover and by a screen reader — already formatted. */
  label: string;
};

const STEPS = ['bg-heat1', 'bg-heat2', 'bg-heat3', 'bg-heat4', 'bg-heat5'] as const;

export type HeatGridProps = {
  /** Columns of 7, oldest first. */
  weeks: readonly (readonly HeatCell[])[];
  /** Column index → month label, already formatted by the caller. */
  months: readonly { column: number; label: string }[];
  /** Names the whole grid for assistive tech. */
  label: string;
  /** "Less" / "More" for the scale legend. */
  legend: { low: string; high: string };
  className?: string;
};

/**
 * A year at a glance.
 *
 * A sequential scale, so intensity is the share of the day's promises kept —
 * never minutes. Minutes would reward the one marathon Sunday over six kept
 * weekdays, which is the opposite of what the product is for.
 *
 * The distinction this grid exists to preserve: **a day with nothing promised
 * looks different from a day promised and missed.** They are both "zero", and
 * collapsing them would render every rest day as a failure and turn a year
 * into an indictment. Rest is recessed; a real miss carries the same neutral
 * outline the wall chart uses.
 *
 * Ships with a legend because the ramp is two hues. Clay and ochre are
 * analogous neighbours so the run reads as one rising temperature, and the
 * order is carried by lightness — tested to survive all three simulated colour
 * blindnesses — but a multi-hue sequential scale still owes the reader a key.
 */
export function HeatGrid({ weeks, months, label, legend, className }: HeatGridProps) {
  const monthAt = new Map(months.map((month) => [month.column, month.label]));

  return (
    <VStack gap={2} className={cx('min-w-0', className)}>
      <Box className="overflow-x-auto">
        <VStack gap={1} className="w-max">
          {/* Month labels ride above their own column and are allowed to
              overflow to the right — a 3-letter month is wider than a 10px
              track, and shrinking the tracks to fit text would shrink the
              year. */}
          <HStack gap={1} aria-hidden>
            {weeks.map((_, column) => (
              <Box key={column} className="w-2.5 shrink-0">
                {monthAt.has(column) && (
                  <Text variant="caption" tone="subtle" className="whitespace-nowrap">
                    {monthAt.get(column)}
                  </Text>
                )}
              </Box>
            ))}
          </HStack>

          <HStack gap={1} role="img" aria-label={label}>
            {weeks.map((week, column) => (
              <VStack key={column} gap={1} className="shrink-0">
                {week.map((cell, row) => (
                  <Box
                    key={row}
                    className={cx(
                      'size-2.5 rounded-[2px]',
                      cellClass(cell),
                      // Today keeps a ring so the eye can find "now" in a wall
                      // of 371 squares.
                      cell.isToday && 'ring-1 ring-ink ring-offset-1 ring-offset-surface',
                    )}
                    {...(cell.future ? {} : { title: cell.label })}
                  />
                ))}
              </VStack>
            ))}
          </HStack>
        </VStack>
      </Box>

      <HStack gap={1} align="center" className="self-end">
        <Text variant="caption" tone="subtle">
          {legend.low}
        </Text>
        {/* Written out rather than built from an index: Tailwind scans source
            for whole class names, so `bg-heat${n}` would generate nothing and
            the legend would be five invisible squares. */}
        {STEPS.map((step) => (
          <Box key={step} className={cx('size-2.5 rounded-[2px]', step)} />
        ))}
        <Text variant="caption" tone="subtle">
          {legend.high}
        </Text>
      </HStack>
    </VStack>
  );
}

/**
 * Five steps, plus the two kinds of nothing.
 *
 * Bucketed rather than continuous: 371 cells at arbitrary opacity is noise,
 * and five steps is what a reader can actually hold against a legend.
 */
function cellClass(cell: HeatCell): string {
  if (cell.future) return 'bg-sunken opacity-40';
  // Nothing was asked of this day. Recessed, so the eye skips it — the same
  // treatment an off-schedule cell gets in the wall chart.
  if (!cell.promised) return 'bg-sunken';
  // Asked and not done. Neutral and filled, never red: the year is a record,
  // not a scolding.
  if (cell.ratio <= 0) return 'bg-line';
  if (cell.ratio < 0.25) return 'bg-heat1';
  if (cell.ratio < 0.5) return 'bg-heat2';
  if (cell.ratio < 0.75) return 'bg-heat3';
  if (cell.ratio < 1) return 'bg-heat4';
  return 'bg-heat5';
}
